import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../../context/ChatContext';
import { phraseSetForRole, phraseSets } from '../../data/phraseSets';
import {
  loadPlatePhrases,
  phrasePlateId,
  savePlatePhrases,
} from '../../services/plateStorage';
import { speakPhrase } from '../../services/phraseSpeech';
import './VocabularyPanel.css';

const MY_PLATE_ID = 'my-plate';

const VocabularyPanel = ({ open, onClose }) => {
  const { chatRole } = useChat();
  const [hideEnglish, setHideEnglish] = useState(false);
  const [selectedId, setSelectedId] = useState(() => phraseSetForRole(chatRole).id);
  const [playingKey, setPlayingKey] = useState(null);
  const [plating, setPlating] = useState(false);
  const [platePhrases, setPlatePhrases] = useState(() => loadPlatePhrases());
  const playToken = useRef(0);
  const lastRoleRef = useRef(chatRole);

  const plateIds = useMemo(
    () => new Set(platePhrases.map(phrasePlateId)),
    [platePhrases]
  );

  const allSets = useMemo(
    () => [
      {
        id: MY_PLATE_ID,
        title: `My plate (${platePhrases.length})`,
        groups: [
          {
            id: 'plate',
            title: platePhrases.length
              ? 'Saved for practice'
              : 'Empty — tap + then add phrases from any section',
            phrases: platePhrases,
          },
        ],
      },
      ...phraseSets,
    ],
    [platePhrases]
  );

  const playPhrase = (key, spoken) => {
    if (!spoken) return;
    const token = playToken.current + 1;
    playToken.current = token;
    setPlayingKey(key);
    speakPhrase(spoken).finally(() => {
      if (playToken.current === token) {
        setPlayingKey(null);
      }
    });
  };

  const persistPlate = (next) => {
    setPlatePhrases(next);
    savePlatePhrases(next);
  };

  const addToPlate = (phrase) => {
    const id = phrasePlateId(phrase);
    if (plateIds.has(id)) return;
    persistPlate([
      ...platePhrases,
      {
        english: phrase.english,
        mandarin: phrase.mandarin,
        spoken: phrase.spoken,
      },
    ]);
  };

  const removeFromPlate = (phrase) => {
    const id = phrasePlateId(phrase);
    persistPlate(platePhrases.filter((item) => phrasePlateId(item) !== id));
  };

  const clearPlate = () => {
    persistPlate([]);
  };

  // Snap to the LARP scene only when the role changes — not on every open,
  // so close/reopen keeps the user's section and scroll position.
  useEffect(() => {
    if (lastRoleRef.current === chatRole) return;
    lastRoleRef.current = chatRole;
    setSelectedId(phraseSetForRole(chatRole).id);
  }, [chatRole]);

  const activeSet = allSets.find((set) => set.id === selectedId) || allSets[0];
  const viewingPlate = activeSet.id === MY_PLATE_ID;
  let phraseNumber = 0;

  return (
    <aside
      className={`vocabulary-panel${hideEnglish ? ' english-hidden' : ''}${open ? '' : ' is-closed'}${plating ? ' is-plating' : ''}`}
      aria-label="Mandarin phrases"
      aria-hidden={!open}
    >
      <div className="vocabulary-panel-header">
        <div className="vocabulary-panel-heading">
          <h2 className="vocabulary-panel-title">{activeSet.title}</h2>
          <p className="vocabulary-panel-subtitle">
            {plating
              ? 'Tap + on a line to add it to My plate'
              : viewingPlate
                ? 'Your saved phrases — remove any you no longer need'
                : 'Click a line to hear it'}
          </p>
        </div>
        <div className="vocabulary-panel-actions">
          <button
            type="button"
            className={`vocabulary-plate-toggle ${plating ? 'active' : ''}`}
            aria-pressed={plating}
            aria-label={plating ? 'Done adding to My plate' : 'Add phrases to My plate'}
            title={plating ? 'Done' : 'Add to My plate'}
            onClick={() => setPlating((on) => !on)}
          >
            {plating ? 'Done' : '+'}
          </button>
          <button
            type="button"
            className={`vocabulary-english-toggle ${hideEnglish ? 'active' : ''}`}
            aria-pressed={hideEnglish}
            onClick={() => setHideEnglish((hidden) => !hidden)}
          >
            {hideEnglish ? 'Show English' : 'Hide English'}
          </button>
          <button
            type="button"
            className="vocabulary-panel-close"
            onClick={onClose}
            aria-label="Close vocabulary"
          >
            ×
          </button>
        </div>
      </div>

      <div className="vocabulary-panel-toolbar">
        <label className="vocabulary-set-label" htmlFor="phrase-set">
          Section
        </label>
        <div className="vocabulary-toolbar-row">
          <select
            id="phrase-set"
            className="vocabulary-set-select"
            value={activeSet.id}
            onChange={(event) => setSelectedId(event.target.value)}
          >
            {allSets.map((set) => (
              <option key={set.id} value={set.id}>
                {set.title}
              </option>
            ))}
          </select>
          {viewingPlate && platePhrases.length > 0 && (
            <button
              type="button"
              className="vocabulary-plate-clear"
              onClick={clearPlate}
              aria-label="Clear My plate"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="vocabulary-panel-body">
        {activeSet.groups.map((group) => (
          <section key={group.id} className="vocabulary-group">
            <h3 className="vocabulary-group-title">{group.title}</h3>
            {group.phrases.length === 0 ? (
              <p className="vocabulary-plate-empty">
                Open any section, tap +, then tap + on phrases you want here.
              </p>
            ) : (
              <ol className="vocabulary-list">
                {group.phrases.map((phrase) => {
                  phraseNumber += 1;
                  const number = phraseNumber;
                  const key = `${group.id}-${number}`;
                  const playing = playingKey === key;
                  const plateId = phrasePlateId(phrase);
                  const onPlate = plateIds.has(plateId);

                  return (
                    <li key={key}>
                      <div
                        className={`vocabulary-row-wrap${playing ? ' is-playing' : ''}${onPlate && plating ? ' on-plate' : ''}`}
                      >
                        <button
                          type="button"
                          className="vocabulary-row"
                          onClick={() => playPhrase(key, phrase.spoken)}
                          aria-label={`Hear ${phrase.english}`}
                        >
                          <span className="vocabulary-number">{number}</span>
                          {!hideEnglish && (
                            <span className="vocabulary-english">{phrase.english}</span>
                          )}
                          <span className="vocabulary-mandarin">{phrase.mandarin}</span>
                        </button>
                        {plating && !viewingPlate && (
                          <button
                            type="button"
                            className={`vocabulary-plate-action ${onPlate ? 'is-added' : ''}`}
                            aria-label={
                              onPlate
                                ? `Already on My plate: ${phrase.english}`
                                : `Add to My plate: ${phrase.english}`
                            }
                            disabled={onPlate}
                            onClick={() => addToPlate(phrase)}
                          >
                            {onPlate ? '✓' : '+'}
                          </button>
                        )}
                        {viewingPlate && (
                          <button
                            type="button"
                            className="vocabulary-plate-action is-remove"
                            aria-label={`Remove from My plate: ${phrase.english}`}
                            onClick={() => removeFromPlate(phrase)}
                          >
                            −
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        ))}
      </div>
    </aside>
  );
};

export default VocabularyPanel;
